import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { COMMON_TIME_ZONES, browserTimeZone, zonedTimeToUtcIso } from '@/services/dialer/callbackSchedule';

/**
 * RUI-05: choosing "Rappel demandé" asks when. Date, time and time zone are
 * all required; the result is the UTC instant written to `follow_up_at`,
 * which is what Tâches lists.
 */
interface Props {
  open: boolean;
  saving?: boolean;
  onCancel: () => void;
  onConfirm: (followUpAtIso: string) => void;
}

function tomorrow(): string {
  const d = new Date(Date.now() + 24 * 3600 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function CallbackScheduleDialog({ open, saving = false, onCancel, onConfirm }: Props) {
  const { t } = useTranslation(['dialer']);
  const ownZone = browserTimeZone();
  const zones = (COMMON_TIME_ZONES as readonly string[]).includes(ownZone) ? [...COMMON_TIME_ZONES] : [ownZone, ...COMMON_TIME_ZONES];
  const [date, setDate] = useState(tomorrow);
  const [time, setTime] = useState('10:00');
  const [zone, setZone] = useState(ownZone);
  const iso = zonedTimeToUtcIso(date, time, zone);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent className="sm:max-w-md">
        {/* React events cross the portal: keep clicks off the call row below. */}
        <div onClick={(e) => e.stopPropagation()} className="space-y-4">
        <DialogHeader>
          <DialogTitle>{t('dialer:qualification.callback.title')}</DialogTitle>
          <DialogDescription>{t('dialer:qualification.callback.description')}</DialogDescription>
        </DialogHeader>
        <form
          className="grid grid-cols-2 gap-3"
          onSubmit={(e) => { e.preventDefault(); if (iso) onConfirm(iso); }}
        >
          <div className="space-y-1">
            <Label htmlFor="callback-date">{t('dialer:qualification.callback.date')}</Label>
            <Input id="callback-date" type="date" required value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="callback-time">{t('dialer:qualification.callback.time')}</Label>
            <Input id="callback-time" type="time" required value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>{t('dialer:qualification.callback.timeZone')}</Label>
            <Select value={zone} onValueChange={setZone}>
              <SelectTrigger aria-label={t('dialer:qualification.callback.timeZone')}><SelectValue /></SelectTrigger>
              <SelectContent>
                {zones.map((z) => <SelectItem key={z} value={z}>{z.replace(/_/g, ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!iso && (
            <p role="alert" className="col-span-2 text-xs text-destructive">{t('dialer:qualification.callback.invalid')}</p>
          )}
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={onCancel}>{t('dialer:qualification.callback.cancel')}</Button>
            <Button type="submit" disabled={!iso || saving}>{t('dialer:qualification.callback.confirm')}</Button>
          </DialogFooter>
        </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
